import React from 'react';
import { motion } from 'framer-motion';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    'Company': [
      { name: 'About Us', href: '#who-we-are' },
      { name: 'Our Mission', href: '#what-is-vbc' },
      { name: 'Team', href: '#who-we-are' },
      { name: 'Careers', href: '#contact' },
    ],
    'Services': [
      { name: 'Investments', href: '#what-is-vbc' },
      { name: 'Venture Building', href: '#what-is-vbc' },
      { name: 'Advisory', href: '#what-is-vbc' },
      { name: 'Portfolio', href: '#what-is-vbc' },
    ],
    'Resources': [
      { name: 'Blog', href: '#contact' },
      { name: 'Case Studies', href: '#contact' },
      { name: 'Reports', href: '#contact' },
      { name: 'Events', href: '#contact' },
    ],
  };


  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId.replace('#', ''));
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-navy-950 border-t border-white/10">
      {/* Pre-footer CTA */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="py-16 bg-gradient-to-r from-secondary-600 to-primary-600"
      >
        <div className="max-w-4xl mx-auto text-center px-6 lg:px-8">
          <h3 className="text-3xl font-bold text-white mb-4">
            Ready to Build the Future?
          </h3>
          <p className="text-xl text-white/90 mb-8">
            Join the VBC network and transform your innovative ideas into market-leading ventures.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => scrollToSection('contact')}
              className="bg-white text-secondary-600 font-semibold px-8 py-3 rounded-xl hover:bg-gray-100 transition-all duration-300 transform hover:scale-105"
            >
              Get Started Today
            </button>
            <button
              onClick={() => scrollToSection('what-is-vbc')}
              className="text-white font-semibold px-8 py-3 rounded-xl border-2 border-white/30 hover:border-white hover:bg-white/10 transition-all duration-300"
            >
              Learn More
            </button>
          </div>
        </div>
      </motion.div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Brand Column */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="lg:col-span-1"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-lg">
                <img 
                  src="/vbc-logo.svg" 
                  alt="VBC Logo" 
                  className="w-8 h-8 object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <span className="text-navy-900 font-bold text-lg hidden">V</span>
              </div>
              <span className="text-xl font-bold text-white">VBC</span>
            </div>
            <p className="text-gray-400 leading-relaxed mb-6">
              Venture Builders Collective - Where innovation meets opportunity. 
              Empowering entrepreneurs and building the future of business.
            </p>
            
            {/* Left-aligned GPS Pin Icon */}
            <div className="flex justify-start mb-6">
              <div className="text-secondary-400">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20" stroke="currentColor" strokeWidth="0.5">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            
            {/* Address Text */}
            <p className="text-gray-400 leading-relaxed text-sm mb-6">
              DIFC Innovation Hub, Sheikh Zayed Road, Dubai International Financial Centre, Dubai, United Arab Emirates
            </p>
          </motion.div>

          {/* Links Columns */}
          {Object.entries(footerLinks).map(([category, links], index) => (
            <motion.div
              key={category}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <h4 className="text-lg font-semibold text-white mb-6">{category}</h4>
              <ul className="space-y-3">
                {links.map((link) => (
                  <li key={link.name}>
                    <button
                      onClick={() => scrollToSection(link.href)}
                      className="text-gray-400 hover:text-secondary-400 transition-colors duration-300 text-left"
                    >
                      {link.name}
                    </button>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="border-t border-white/10 mt-16 pt-8 flex flex-col md:flex-row items-center justify-between"
        >
          <p className="text-gray-400 text-sm">
            © {currentYear} Venture Builders Collective. All rights reserved.
          </p>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <button className="text-gray-400 hover:text-white text-sm transition-colors duration-300">
              Privacy Policy
            </button>
            <button className="text-gray-400 hover:text-white text-sm transition-colors duration-300">
              Terms of Service
            </button>
            <button className="text-gray-400 hover:text-white text-sm transition-colors duration-300">
              Cookie Policy
            </button>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;